
import React from 'react';
import { format } from 'date-fns';
import { generateInvoiceFooterText } from '@/utils/invoiceFooterUtils';
import InvoiceProjectProgressBar from './InvoiceProjectProgressBar';
import { formatCurrency } from '@/lib/utils';
import '@/components/estimates/preview/pdf-styles.css';

interface InvoicePdfStylePreviewProps {
  invoice: any;
  lineItems: any[];
  customer: any;
  company: any;
  userProfile: any;
  documentDefaults?: any;
  className?: string;
  scale?: number;
}

const InvoicePdfStylePreview: React.FC<InvoicePdfStylePreviewProps> = ({
  invoice,
  lineItems,
  customer,
  company,
  userProfile,
  documentDefaults,
  className = '',
  scale = 0.75
}) => {
  
  const fontStyles = { 
    fontFamily: 'Arial, sans-serif',
    fontSize: '11px',
    lineHeight: '1.15'
  };

  // Format currency with space separators (South African format)

  // Format phone number to xxx xxx xxxx format
  const formatPhoneNumber = (phone: string | undefined): string => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    }
    return phone;
  };

  // Safe display for potentially null values
  const safeDisplay = (value: string | undefined | null, fallback: string = 'N/A'): string => {
    return value || fallback;
  };

  // Get status display information
  const getStatusDisplay = () => {
    const status = invoice.status?.toLowerCase();
    // Invoice is overdue only after the due date has completely passed (after 23:59:59)
    const isOverdue = invoice.due_date && new Date() > new Date(invoice.due_date + 'T23:59:59') && status !== 'paid';
    
    if (status === 'paid') {
      return {
        text: 'PAID',
        headingText: 'AMOUNT PAID',
        color: '#16a34a', // green-600
        bgColor: '#dcfce7', // green-100
        borderColor: '#16a34a',
        cssClass: 'amount-due-paid'
      };
    } else if (isOverdue) {
      return {
        text: 'OVERDUE',
        headingText: 'AMOUNT DUE',
        color: '#dc2626', // red-600
        bgColor: '#fee2e2', // red-100
        borderColor: '#dc2626',
        cssClass: 'amount-due-overdue'
      };
    } else if (status === 'sent') {
      return {
        text: 'UNPAID',
        headingText: 'AMOUNT DUE',
        color: '#ea580c', // orange-600
        bgColor: '#fed7aa', // orange-100
        borderColor: '#ea580c',
        cssClass: 'amount-due-unpaid'
      };
    } else if (status === 'draft') {
      return {
        text: 'DRAFT',
        headingText: 'AMOUNT DUE',
        color: '#6b7280', // gray-500
        bgColor: '#f3f4f6', // gray-100
        borderColor: '#6b7280',
        cssClass: 'amount-due-draft'
      };
    } else if (status === 'cancelled') {
      return {
        text: 'CANCELLED',
        headingText: 'AMOUNT DUE',
        color: '#dc2626', // red-600
        bgColor: '#fee2e2', // red-100
        borderColor: '#dc2626',
        cssClass: 'amount-due-cancelled'
      };
    } else {
      return {
        text: 'DUE',
        headingText: 'AMOUNT DUE',
        color: '#2563eb', // blue-600
        bgColor: '#dbeafe', // blue-100
        borderColor: '#2563eb',
        cssClass: 'amount-due-due'
      };
    }
  };

  const statusDisplay = getStatusDisplay();
  const subtotal = invoice.subtotal || 0;
  const taxAmount = invoice.tax_amount || 0;
  const totalAmount = invoice.total_amount || 0;
  const creditApplied = invoice.credit_applied || 0;
  const netAmountDue = totalAmount - creditApplied;
  const paidAmount = invoice.paid_amount || 0;
  const balanceDue = netAmountDue - paidAmount;
  const isVatRegistered = company?.is_vat_registered ?? false;

  // Check if this is a percentage invoice
  const isPercentageInvoice = invoice.invoice_type === 'percentage' && invoice.percentage_of_estimate;

  // Calculate project financial summary - only show PAID amounts as previously invoiced
  const estimateTotal = invoice.estimates?.total_amount || 0;
  const totalPaidByProject = invoice.estimates?.total_paid || 0; // Only count actually paid invoices
  // For remaining calculation, we still need to consider all invoiced amounts to avoid double counting
  const totalInvoicedByProject = invoice.estimates?.total_invoiced || 0;
  const remainingToInvoice = Math.max(0, estimateTotal - totalPaidByProject - invoice.total_amount);

  // Generate footer text using shared utility
  const footerText = generateInvoiceFooterText({
    invoice,
    customer,
    company,
    userProfile,
    estimate: invoice.estimates
  }, documentDefaults);

  // Render page header (company logo)
  const renderPageHeader = () => (
    <div className="page-header-for-pdf" style={{ height: '120px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start' }}>
      {company?.logo_url && (
        <img 
          src={company.logo_url} 
          alt="Company Logo" 
          className="page-logo-for-pdf"
          style={{ maxWidth: '400px', maxHeight: '150px', objectFit: 'contain' }}
        />
      )}
    </div>
  );

  // Render page footer
  const renderPageFooter = () => (
    <div className="page-footer-for-pdf" style={{ 
      marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #ccc',
      fontSize: '9px', fontStyle: 'italic', fontWeight: 'bold',
      color: 'black', lineHeight: '1.4'
    }}>
      <div>{footerText}</div>
    </div>
  );

  return (
    <div className={`pdf-preview-wrapper ${className}`}>
      <div className="pdf-page-container">
        <div className="pdf-paper">
          <div className="pdf-margins">
            {/* Header */}
            {renderPageHeader()}

            {/* Main Content */}
            <div className="pdf-content" id="invoice-preview" style={fontStyles}>
        {/* Header Section - Reordered Layout */}
        <div className="mb-8 no-page-break">
          {/* Invoice Info - Top Right */}
          <div className="flex justify-end mb-6">
            <div className="text-right">
              <h2 className="text-xl font-bold text-gray-900">INVOICE</h2>
              
              {/* Status Badge */}
              <div 
                className="inline-block mt-2 mb-3 px-3 py-1 rounded text-sm font-bold border-2"
                style={{
                  color: statusDisplay.color,
                  backgroundColor: statusDisplay.bgColor,
                  borderColor: statusDisplay.borderColor
                }}
              >
                {statusDisplay.text}
              </div>
              
              <div className="text-sm text-gray-600 mt-2">
                <div><strong>Invoice #:</strong> {invoice.invoice_number}</div>
                <div><strong>Date:</strong> {format(new Date(invoice.invoice_date || invoice.created_at), 'dd MMMM yyyy')}</div>
                {invoice.due_date && (
                  <div><strong>Due Date:</strong> {format(new Date(invoice.due_date), 'dd MMMM yyyy')}</div>
                )}
                {isPercentageInvoice && (
                  <div><strong>Invoice Type:</strong> {invoice.percentage_of_estimate}% of Estimate</div>
                )}
              </div>
            </div>
          </div>

          {/* Company Info - Left Column */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">{company?.name || 'Company Name'}</h1>
            <div className="text-sm text-gray-600 mt-2">
              {company?.street_address && <div>{company.street_address}</div>}
              {company?.suburb && <div>{company.suburb}</div>}
              {company?.city && <div>{company.city}, {company.province_state} {company.postal_code}</div>}
              {company?.company_phone_number && (
                <div><strong>Tel:</strong> {formatPhoneNumber(company.company_phone_number)}</div>
              )}
              {company?.email && (
                <div><strong>Email:</strong> {safeDisplay(company.email)}</div>
              )}
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="mb-8 no-page-break">
          <h3 className="text-lg font-semibold mb-4">Bill To:</h3>
          <div className="text-sm">
            <div className="font-medium">
              {customer?.customer_type === 'commercial' && customer?.business_name
                ? customer.business_name
                : `${customer?.name} ${customer?.last_name}`}
            </div>
            {invoice?.use_separate_site_address && invoice?.site_street_address ? (
              <>
                <div>{invoice.site_street_address}</div>
                {invoice.site_suburb && <div>{invoice.site_suburb}</div>}
                {invoice.site_city && <div>{invoice.site_city}{invoice.site_province_state ? `, ${invoice.site_province_state}` : ''}{invoice.site_postal_code ? ` ${invoice.site_postal_code}` : ''}</div>}
              </>
            ) : (
              <>
                {customer?.street_address && <div>{customer.street_address}</div>}
                {customer?.suburb && <div>{customer.suburb}</div>}
                {customer?.city && <div>{customer.city}, {customer.province_state} {customer.postal_code}</div>}
              </>
            )}
            {/* Show VAT Reg No. for commercial customers - after address */}
            {customer?.customer_type === 'commercial' && customer?.vat_number && (
              <div><strong>VAT Reg No.:</strong> {customer.vat_number}</div>
            )}
            <div><strong>Phone:</strong> {formatPhoneNumber(
              customer?.customer_type === 'commercial' && customer?.business_phone
                ? customer.business_phone
                : customer?.cell_numbers?.[0]
            )}</div>
            <div><strong>Email:</strong> {safeDisplay(
              customer?.customer_type === 'commercial' && customer?.business_email
                ? customer.business_email
                : customer?.emails?.[0]
            )}</div>
          </div>
        </div>

        {/* Percentage Invoice Notice */}
        {isPercentageInvoice && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded no-page-break">
            <p className="text-sm font-semibold text-blue-800">
              This invoice represents {invoice.percentage_of_estimate}% of the total estimate amount. 
              Full quantities and rates are shown below, but only the percentage amount is being invoiced.
            </p>
          </div>
        )}

        {/* Line Items Table */}
        <div className="mb-8">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left"><strong>Description</strong></th>
                <th className="border border-gray-300 px-4 py-2 text-right"><strong>Quantity</strong></th>
                <th className="border border-gray-300 px-4 py-2 text-right"><strong>Unit Price</strong></th>
                <th className="border border-gray-300 px-4 py-2 text-right"><strong>Total</strong></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-2">{item.description}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">
                    {item.quantity_invoiced} {item.base_unit}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-right">
                    {formatCurrency(item.unit_cost)}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-right">
                    {formatCurrency(item.total_cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PROMINENT AMOUNT DUE/PAID SECTION - Smaller size with status-colored border */}
        <div className="mb-6 no-page-break">
          <div 
            className={`amount-due-section p-2 border-2 rounded-lg bg-gray-50 ${statusDisplay.cssClass}`}
            style={{
              borderColor: statusDisplay.borderColor,
              backgroundColor: statusDisplay.bgColor
            }}
          >
            <div className="text-center">
              <h3 className="text-sm font-bold mb-1 text-gray-800">{statusDisplay.headingText}</h3>
              
              {/* Show breakdown if VAT registered, otherwise show simple amount */}
              {isVatRegistered && taxAmount > 0 ? (
                <div className="mb-1">
                  <div className="text-xs mb-1">
                    <div className="flex justify-between items-center max-w-48 mx-auto mb-1">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex justify-between items-center max-w-48 mx-auto mb-1">
                      <span>VAT ({invoice.tax_percentage || 15}%):</span>
                      <span>{formatCurrency(taxAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center max-w-48 mx-auto font-bold mb-1">
                      <span>Total:</span>
                      <span>{formatCurrency(totalAmount)}</span>
                    </div>
                    {creditApplied > 0 && (
                      <>
                        <div className="flex justify-between items-center max-w-48 mx-auto text-blue-600 mb-1">
                          <span>Credits Applied:</span>
                          <span>-{formatCurrency(creditApplied)}</span>
                        </div>
                        <div className="border-t border-gray-400 pt-1">
                          <div className="flex justify-between items-center max-w-48 mx-auto font-bold text-sm">
                            <span>Net Amount:</span>
                            <span style={{ color: statusDisplay.color }}>{formatCurrency(netAmountDue)}</span>
                          </div>
                        </div>
                      </>
                    )}
                    {creditApplied <= 0 && (
                      <div className="border-t border-gray-400 pt-1">
                        <div className="flex justify-between items-center max-w-48 mx-auto font-bold text-sm">
                          <span>Amount Due:</span>
                          <span style={{ color: statusDisplay.color }}>{formatCurrency(totalAmount)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mb-1">
                  {creditApplied > 0 ? (
                    <div className="text-xs">
                      <div className="flex justify-between items-center max-w-48 mx-auto mb-1">
                        <span>Total:</span>
                        <span>{formatCurrency(totalAmount)}</span>
                      </div>
                      <div className="flex justify-between items-center max-w-48 mx-auto text-blue-600 mb-1">
                        <span>Credits Applied:</span>
                        <span>-{formatCurrency(creditApplied)}</span>
                      </div>
                      <div className="border-t border-gray-400 pt-1">
                        <div 
                          className="flex justify-between items-center max-w-48 mx-auto font-bold text-sm"
                          style={{ color: statusDisplay.color }}
                        >
                          <span>{invoice.status === 'paid' ? 'Amount Paid:' : 'Net Due:'}</span>
                          <span>
                            {invoice.status === 'paid' 
                              ? formatCurrency(paidAmount || netAmountDue)
                              : formatCurrency(balanceDue)
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="text-base font-bold"
                      style={{ color: statusDisplay.color }}
                    >
                      {invoice.status === 'paid' 
                        ? formatCurrency(paidAmount || totalAmount)
                        : formatCurrency(totalAmount - paidAmount)
                      }
                    </div>
                  )}
                </div>
              )}
              
              {invoice.due_date && (
                <div className="text-xs font-semibold text-gray-700">
                  Due: {format(new Date(invoice.due_date), 'dd MMMM yyyy')}
                </div>
              )}
              {paidAmount > 0 && invoice.status !== 'paid' && (
                <div className="text-xs text-green-600 mt-1">
                  Amount Paid: {formatCurrency(paidAmount)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Terms */}
        {invoice.payment_terms && (
          <div className="mb-6 no-page-break">
            <h3 className="text-lg font-semibold mb-2">Payment Terms:</h3>
            <p className="text-sm text-gray-700">{invoice.payment_terms}</p>
          </div>
        )}

        {/* Special Instructions */}
        {invoice.special_instructions && (
          <div className="mb-6 no-page-break">
            <h3 className="text-lg font-semibold mb-2">Special Instructions:</h3>
            <p className="text-sm text-gray-700">{invoice.special_instructions}</p>
          </div>
        )}

        {/* Banking Information */}
        {company?.["Bank Name"] && (
          <div className="mb-6 no-page-break">
            <h3 className="text-lg font-semibold mb-2">Banking Details:</h3>
            <div className="text-sm text-gray-700">
              <div><strong>Bank:</strong> {company["Bank Name"]}</div>
              <div><strong>Account Name:</strong> {company["Account Holder"]}</div>
              <div><strong>Account Number:</strong> {company["Account Number"]}</div>
              <div><strong>Branch Code:</strong> {company["Branch Code"]}</div>
              {invoice?.invoice_number && (
                <div>
                  <strong>Payment Ref:</strong>{' '}
                  <span style={{ fontWeight: 'bold', color: '#036CFF' }}>
                    {invoice.invoice_number}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Project Invoice Progress Bar - Show when this is not a single full invoice */}
        {estimateTotal > 0 && estimateTotal > totalAmount && (
          <div className="mb-6">
            <InvoiceProjectProgressBar
              estimateTotal={estimateTotal}
              totalPaidPreviously={totalPaidByProject}
              currentInvoiceAmount={totalAmount}
              remainingToInvoice={remainingToInvoice}
            />
          </div>
        )}

        {/* Invoice Footer */}
            </div>

            {/* Page Footer with dynamic content */}
            {renderPageFooter()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePdfStylePreview;
